import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, Shield } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.__BACKEND_URL;
const LEGACY_TYPES = ['terms-of-service', 'privacy', 'tos', 'privacy-policy'];

export default function LegalDocument() {
  const { type } = useParams();
  const navigate = useNavigate();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isVault, setIsVault] = useState(false);

  useEffect(() => {
    loadDocument();
  }, [type]);

  const loadDocument = async () => {
    try {
      setLoading(true);
      const isLegacy = LEGACY_TYPES.includes(type);
      setIsVault(!isLegacy);

      if (isLegacy) {
        const docType = type === 'terms-of-service' || type === 'tos' ? 'tos' : 'privacy';
        const response = await axios.get(`${API_URL}/api/public/legal/document/${docType}`);
        setDocument(response.data);
      } else {
        const response = await axios.get(`${API_URL}/api/public/legal/vault/${type}`);
        setDocument(response.data);
      }
    } catch (err) {
      console.error('Error loading document:', err);
      toast.error('Failed to load document');
      setDocument(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#121417] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="min-h-screen bg-[#121417] flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 mb-4">Document not found</p>
          <button
            onClick={() => navigate('/legal')}
            className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors"
          >
            Back to Legal Center
          </button>
        </div>
      </div>
    );
  }

  const updatedAt = document.updated_at || document.last_updated_at || document.created_at;

  return (
    <div className="min-h-screen bg-[#121417] py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/legal')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Legal Center
          </button>

          <h1 className="text-4xl font-bold text-white mb-4">{document.title}</h1>

          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
            {updatedAt && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>Last updated: {new Date(updatedAt).toLocaleDateString()}</span>
              </div>
            )}
            {isVault && (
              <div className="flex items-center gap-2 text-green-300">
                <Shield className="w-4 h-4" />
                <span>View-only</span>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        {isVault && document.content ? (
          <div className="bg-[#1a1d23] border border-gray-800 rounded-lg p-8 mb-8">
            <div
              className="prose prose-invert prose-cyan max-w-none whitespace-pre-line"
              dangerouslySetInnerHTML={{ 
                __html: document.content
                  .replace(/^# (.*$)/gm, '<h1 class="text-3xl font-bold mb-4">$1</h1>')
                  .replace(/^## (.*$)/gm, '<h2 class="text-2xl font-semibold mt-6 mb-3">$1</h2>')
                  .replace(/^- (.*$)/gm, '<li class="ml-4">$1</li>')
                  .replace(/\n/g, '<br/>')
              }}
            />
          </div>
        ) : isVault ? (
          <div className="bg-[#1a1d23] border border-gray-800 rounded-lg p-4 mb-6">
            <div className="aspect-[3/4] w-full">
              <iframe
                title={document.title}
                src={`${API_URL}/api/public/legal/vault/${type}/stream`}
                className="w-full h-[80vh] border-0 rounded-md"
              />
            </div>
          </div>
        ) : (
          <div className="bg-[#1a1d23] border border-gray-800 rounded-lg p-8 mb-8">
            <div
              className="prose prose-invert prose-cyan max-w-none"
              dangerouslySetInnerHTML={{ __html: document.content_html }}
            />
          </div>
        )}

        {/* LLC Notice */}
        {!isVault && (
          <div className="p-6 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <h4 className="text-lg font-semibold text-yellow-300 mb-2">LLC Transfer Notice</h4>
            <p className="text-yellow-400/90 text-sm">
              This service is operated by New Era Tech Group, LLC (Company-in-Formation).
              Upon formation of Consistency App LLC, all rights and obligations will transfer automatically.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
